/**
 * @file EvenementsCrossfitHyrox.tsx
 * @summary Article de blog — EvenementsCrossfitHyrox
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, FAQList, FurtherReading, SourcesSection, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/evenements-crossfit-hyrox-france-region";
const TITLE = "Événements CrossFit et HYROX en France : calendrier 2026";
const DESC = "Calendrier des principales compétitions CrossFit (Open, Affiliates Battle, Winter Games) et HYROX en France et en région Auvergne-Rhône-Alpes. Comment s'inscrire, comment s'y préparer.";

const author = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };
const reviewer = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };

const faq = [
  { q: "Quelle est la plus accessible pour un débutant ?", a: "L'Open CrossFit (février-mars) reste la plus accessible : 3 semaines, 1 WOD par semaine, format scaled disponible. C'est la porte d'entrée idéale pour découvrir la compétition sans pression." },
  { q: "HYROX, c'est quoi exactement ?", a: "Une compétition fitness standardisée : 8 km de course (1 km à chaque tour) entrecoupés de 8 stations fonctionnelles (ski erg, sled push, burpees broad jumps, rowing, etc.). Format identique partout dans le monde, ce qui permet de comparer ses temps." },
  { q: "Faut-il être affilié à une box pour participer ?", a: "Open CrossFit : oui, l'inscription se fait via une box affiliée. HYROX : non, inscription individuelle ou en équipe directement sur le site officiel." },
  { q: "Quel niveau pour HYROX ?", a: "Toutes les catégories existent : Open (~1h45-2h pour des hommes), Pro (~1h-1h15 niveau athlétique), Doubles (en duo, partage des stations). Un débutant motivé peut viser un Open après 4 à 6 mois de prépa." },
  { q: "Et les compétitions locales ?", a: "Beaucoup de box organisent des throwdowns ou affiliates battles entre clubs. Plus convivial, format demi-journée, parfait pour faire ses premières armes." },
];

export default function EvenementsCrossfitHyrox() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Événements CrossFit et HYROX en France"
      breadcrumbLabel="Événements CrossFit et HYROX"
      category="Compétition · Événements"
      author={author} reviewer={reviewer}
      publishedAt="2026-12-05" readingMinutes={8}
      ctaVariant="competition"
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-12-05", author, reviewer, faq: faq.map(f => ({ q: f.q, a: typeof f.a === "string" ? f.a : "" })) })}
    >
      <EnBref>
        <p>Le paysage compétition s'est densifié : <strong>CrossFit Open</strong> (février-mars), <strong>HYROX</strong> (saison oct-mai, étapes Lyon, Paris, Bordeaux), <strong>ATHX</strong>, <strong>Affiliates Battle</strong>, <strong>Winter Games</strong>… Il y en a pour tous les niveaux. Voici comment s'y retrouver et comment se préparer.</p>
      </EnBref>

      <h2>Le calendrier des grandes échéances</h2>

      <h3>CrossFit Open (février-mars)</h3>
      <p>L'événement mondial historique. 3 semaines, 1 WOD inédit publié chaque jeudi. Plus de 300 000 participants dans le monde. Inscription via votre box affiliée, scoring en ligne. <strong>Catégories scaled / RX / masters</strong>. C'est le rendez-vous fédérateur de la communauté.</p>

      <h3>HYROX (saison octobre-mai)</h3>
      <p>Format standardisé international, étapes en France à <strong>Lyon, Paris, Bordeaux, Marseille</strong>. Inscription individuelle (Open / Pro), en doubles ou en équipes relais. Temps comparables d'une étape à l'autre. Croissance explosive en France depuis 2023.</p>

      <h3>ATHX (printemps)</h3>
      <p>Compétition française organisée à Paris. Format weekend, plusieurs catégories (Open, Elite, Teams). Niveau plus exigeant que le CrossFit Open, qualifications préalables.</p>

      <h3>Winter Games (décembre-janvier)</h3>
      <p>Compétition régionale CrossFit qui rythme l'hiver. Format en équipe le plus souvent, ambiance très conviviale. Idéale pour une première compétition multi-équipes.</p>

      <h3>Affiliates Battle</h3>
      <p>Format club contre club : votre box affronte d'autres box de la région. Excellent pour souder l'équipe et tester ses limites dans un cadre bienveillant.</p>

      <h3>Throwdowns locaux</h3>
      <p>Demi-journée, organisés par une box hôte, 2 à 4 WOD. Niveau d'entrée idéal — beaucoup de nos adhérents font leur première compétition sur ce format.</p>

      <h2>Comment s'inscrire ?</h2>
      <ul>
        <li><strong>CrossFit Open</strong> — via games.crossfit.com, lien envoyé par votre box.</li>
        <li><strong>HYROX</strong> — directement sur hyrox.com, choisissez l'étape et la catégorie.</li>
        <li><strong>ATHX</strong> — inscription en ligne sur le site de l'organisation, places limitées.</li>
        <li><strong>Throwdowns / Winter Games</strong> — relayés par votre box, places très rapides à partir.</li>
      </ul>

      <h2>Combien de temps pour s'y préparer ?</h2>
      <p>Pour <strong>finir solidement</strong> un Open ou un HYROX Open : 8 à 12 semaines de pratique régulière (3 séances/semaine) suffisent si vous avez déjà une base. Pour <strong>viser un classement</strong> : 16 à 24 semaines de préparation structurée — c'est exactement le format de notre <Link to="/blog/coaching-individuel-competition-hyrox-athx-crossfit">coaching compétition</Link>.</p>

      <FAQList items={faq} />

      <FurtherReading items={[
        { label: "Compétitions CrossFit Open à Lyon", to: "/blog/competitions-crossfit-open-lyon" },
        { label: "Coaching individuel HYROX, ATHX, CrossFit", to: "/blog/coaching-individuel-competition-hyrox-athx-crossfit" },
        { label: "Notre programme Hybrid Training", to: "/cours/hybrid-training" },
      ]} />

      <SourcesSection items={[
        { label: "CrossFit Games — site officiel", href: "https://games.crossfit.com/" },
        { label: "HYROX — site officiel", href: "https://hyrox.com/" },
      ]} />
    </ArticleLayout>
  );
}
